import React, {useEffect, useContext, useState, useCallback} from "react"
import PropTypes from "prop-types"
import {LinkButton} from "../components/styled"

import {AppContext} from "../components/context/AppContext"
import {StartNewSubmissionComponent, TrackerComponent} from "../components/"
import {LoadingSpinner} from "../components/loadingSpinner"

import {ROUTE_PATHS} from "../utils/routes"
import {AuthApi, OfferApi, SubmissionApi} from "../utils/api"
import AnalyticsService from "../utils/AnalyticsService"

import {handleSubmissionError} from "../utils/FormUtils"
import {resetDispatch, getTmoAuthUrl, setDnsSetting} from "../utils/Helpers"

export const Dashboard = ({location, history}) => {
  const {dispatch, state} = useContext(AppContext)
  const [isFetchingUser, setIsFetchingUser] = useState(false)
  const [isFetchingCampaignFromState, setIsFetchingCampaignFromStateCode] = useState(false)
  const [isFetchingSubmissions, setIsFetchingSubmissions] = useState(false)
  const [isReCaptchaVisible, setIsRecaptchaVisible] = useState(false)
  const [hasSubmissions, setHasSubmissions] = useState(false)
  const [showRetryButton, setShowRetryButton] = useState(false)
  const [submissionFetchingErrorMessage, setSubmissionFetchingErrorMessage ] = useState("")
  const [redirectUrl, setRedirectUrl] = useState("")

  const getPromoCodeFromParams = useCallback(() => {
    const searchParams = new URLSearchParams(location.search)
    const redirectStateVariables = searchParams.get("state")
    if (redirectStateVariables) {
      let redirectStateArray = redirectStateVariables.split(":")
      if (redirectStateArray.length === 2 && redirectStateArray[0].toLocaleUpperCase() === "PROMOCODE") {
        return redirectStateArray[1]
      }
    }
  }, [location.search])


  const setDnsCookie = (value) => {
    const date = new Date()
    const expiration = new Date(date.getTime() + 31536000000)
    const cookie = (value.toLowerCase() === "donotsell" ? "dnt=1" : "dnt=0") + ";domain=" + window.location.domain + ";expires=" + expiration.toGMTString()
    document.cookie = cookie
  }

  //REDIRECT URL
  useEffect(() => {
    const getRedirectUrl = () => {
      const promoCode = getPromoCodeFromParams()
      const tmoURL = getTmoAuthUrl(promoCode, state.user.isTFBAccount) 
      setRedirectUrl(tmoURL)
    }

    getRedirectUrl()
  }, [setRedirectUrl, getPromoCodeFromParams, state.campaign.offerId, state.user.isTFBAccount])

  //RECAPTCHA
  useEffect(() => {
    const start = async () => {
      try {
        const result = await OfferApi.getClientReCaptchaVisible()
        setIsRecaptchaVisible(result)
      } catch (error) {
        setShowRetryButton(true)
        setIsFetchingUser(false)
      }
    }

    start()
  }, [])

  //LOGIN
  useEffect(() => {
    const login = async () => {

      const searchParams = new URLSearchParams(location.search)
      const OAuthCode = searchParams.get("code")
      const saml = searchParams.get("samltkn")
      const ban = searchParams.get("ban")
      const userToken = searchParams.get("user_token")
      
      if (!state.user.userToken) {

        try {

          setIsFetchingUser(true)
          AnalyticsService.sendEvent("Upload Site", "Sign In", "dashboard", {event: "signin"})        
          if (OAuthCode){
            //login with OAuth code
            const user = await AuthApi.loginWithTmoId(OAuthCode)
            const dnsSetting = await AuthApi.getGlobalDnsSettingV2(user.tMobileId)
            user.Provider = 'T-Mobile'
            setDnsCookie(dnsSetting.localDoNotSellSetting)
            setDnsSetting(dnsSetting, user.tMobileId)
            dispatch({type: "SETUSER", payload: user})
            setIsFetchingUser(false)
          }
          if (saml) {
            const user = await AuthApi.loginWithSprintToken(saml, state.lookupValues.phoneNumber)
            user.Provider = 'Sprint'
            dispatch({type: "SETUSER", payload: user})
            setIsFetchingUser(false)
          }
          if (ban) {
            // ban-login for testing
            const user = await AuthApi.loginWithBan(ban)
            const provider = searchParams.get("provider")
            user.Provider = provider
            dispatch({type: "SETUSER", payload: user})
            setIsFetchingUser(false)
          }
          if (userToken) {
            // if a user_token is present in the url, 
            // then we have logged in with an HttpHeader directly with the API
            console.log('httpHeader token callback')
            const user = await AuthApi.userTokenHeaderCallback(userToken)
            user.Provider = 'T-Mobile'
            dispatch({type: "SETUSER", payload: user})
            setIsFetchingUser(false)
          }
        } catch (error) {
          setShowRetryButton(true)
          setIsFetchingUser(false)
          setIsFetchingCampaignFromStateCode(false)         
        }        
      }

      if(state.user.userToken) {
        // if we have a promoCode from OAuth state or sesion storage (thank you page),
        // go straight to the found promotion page for the given promo-code campaign
        const promoCodeParams = getPromoCodeFromParams() ? getPromoCodeFromParams() : state.lookupValues.promoCode
        const promoCodeStorage = sessionStorage.getItem("promoCode")

        let promoCode
        if (promoCodeParams || promoCodeStorage) {
          if (promoCodeParams){
            promoCode = promoCodeParams
          }
          else {
            promoCode = promoCodeStorage
          }

          // reset promoCode
          sessionStorage.setItem("promoCode", '')

          // reset submission
          resetDispatch(state, dispatch)
          setIsFetchingCampaignFromStateCode(true)

          //hotfix to fix FirstResponders
          if (!state.lookupValues.phoneNumber){
            state.lookupValues.phoneNumber = state.user.phoneNumbers[0]
          }

          //TFB flow
            if (state.user.isTFBAccount) {
                let values = state.user
                values.primaryPhoneNumber = state.lookupValues.phoneNumber.trim()
                const user = await AuthApi.setTfbUserInfo(values)
                if (!user) {
                    dispatch({ type: "COMMONERROR", payload: "Msisdn doesn't find, please re-enter Msisdn." })
                    return
                }
                dispatch({ type: "SETUSER", payload: user })
                state.user.userToken = user.userToken
                state.user.TFBRole = user.TFBRole
            }

          // do campaign-lookup
          let values = {
            phoneNumber: state.lookupValues.phoneNumber,
            promoCode: promoCode,
            recaptcha: "",
            isTFBAccount: state.user.isTFBAccount,
            userToken: state.user.userToken
          }
          const response = await OfferApi.getOfferDetails(values)

          if (!response.offerId && response.ineligibleReason) {
            setIsFetchingCampaignFromStateCode(false)
            dispatch({type: "COMMONERROR", payload: response.ineligibleReason})
            return
          }

          dispatch({type: "SETCAMPAIGN", payload: response})
          history.push(ROUTE_PATHS.PROMOTION)
        }

        if (!state.user.isTFBAccount || (state.user.isTFBAccount && state.user.ban !== "999999999")) {
          // Only retreive submissions by ban if we intend to stay on this page
          getSubmissions()
        }
      }

      if (!state.user.userToken && !userToken && !ban && !OAuthCode && !saml) {
        history.push(ROUTE_PATHS.HOME)
      }
    }
    login()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.user])

  const cleanAndOrderSubmissions = (unorderedSubmissions) => {
    const mergedSubmissions = [...unorderedSubmissions.submissions, ...unorderedSubmissions.iSubmissions]
    const distinctSubmissions = []
    const map = new Map()

    //get an array of distinct submissions by tracking id
    for (const submission of mergedSubmissions) {
      if (!map.has(submission.trackingId)) {
        map.set(submission.trackingId, true)
        distinctSubmissions.push(submission)
      }
    }

    return distinctSubmissions.sort((sub1, sub2) => {
      if (sub1.stampDate > sub2.stampDate) {
        return -1
      }

      if (sub1.stampDate < sub2.stampDate) {
        return 1
      }

      return 0
    })
  }

  const getSubmissions = async () => {

    try {
      setIsFetchingSubmissions(true)

      if (state.user.ban) {

        const submissions = await SubmissionApi.getSubmissions(state.user)

        const orderedSubmissions = cleanAndOrderSubmissions(submissions)

        dispatch({type: "SETSUBMISSIONS", payload: orderedSubmissions})

        setIsFetchingSubmissions(false)
        setHasSubmissions(orderedSubmissions.length > 0)
      }
    } catch (error) {
      setSubmissionFetchingErrorMessage("Something went wrong, please try again")
      setIsFetchingSubmissions(false)
      setHasSubmissions(false)
    }
  }

  const submitNewSubmissionForm = async (values, {setSubmitting, setStatus}) => {
    try {

      // sprint users search for specific offers get sent to blackhawk sites for now
      if (state.user.Provider === 'SPRINT' && (
        values.promoCode.toUpperCase() === '2020APPLERETAILT1' || values.promoCode.toUpperCase() === '2020APPLERETAIL')){
        
          window.location = 'https://www.sprintpromotionshub.com/#/2020appleretail'
        return
      }

      // starting a new submission
      resetDispatch(state, dispatch)
      
        dispatch({ type: "SETSELECTEDPHONE", payload: values.phoneNumber })

        //TFB flow
        if (state.user.isTFBAccount) {
            let uservalues = state.user
            uservalues.primaryPhoneNumber = values.phoneNumber.trim()
            const user = await AuthApi.setTfbUserInfo(uservalues)
            if (!user) {
                handleSubmissionError(setSubmitting, setStatus, "Msisdn doesn't find, please re-enter Msisdn.")
                return
            }
            dispatch({ type: "SETUSER", payload: user })
            values.isTFBAccount = state.user.isTFBAccount
            values.userToken = user.userToken
        }
      
      const response = await OfferApi.getOfferDetails(values)
      
      if (!response.offerId && response.ineligibleReason) {
        handleSubmissionError(setSubmitting, setStatus, response.ineligibleReason)
        return
      }

      const today = Date.now()

      if (today < Date.parse(response.goLiveDate) || Date.parse(response.expirationDate) < today) {
        handleSubmissionError(setSubmitting, setStatus, "There were no promotions that matched your search criteria.")
        return
      }

      dispatch({type: "SETCAMPAIGN", payload: response})

      AnalyticsService.sendEvent("Upload Site", "Found Promotion", "dashboard", {event: "new_submission"})

        history.push(ROUTE_PATHS.PROMOTION)

    } catch (error) {
      if (error.message === "A promotion with that code does not exist") {
        handleSubmissionError(setSubmitting, setStatus, "There were no promotions that matched your search criteria.")
        return
      }

      handleSubmissionError(setSubmitting, setStatus)
      return
    }
  }

  const sectionStyles = {
    minHeight: "30rem"
  }

  return (
    <>
    {(isFetchingCampaignFromState || isFetchingUser) &&
    <>
    <div className="container hero3">
      <div className="row">
        <div className="col-md-12" style={{textAlign: "center"}}>
          <LoadingSpinner
            className="mb-3"
            enabled={isFetchingCampaignFromState || isFetchingUser}>
          </LoadingSpinner>
          <h2 className="magenta">Loading...</h2><br />
        </div>
      </div>
    </div>
    </>
    }
    {!isFetchingCampaignFromState && !isFetchingUser && showRetryButton &&
      <div>
        <div className="container hero3">
          <div className="row">
            <div className="col-md-12">
              <h1 className="magenta">Oops! Something went wrong.</h1><br />
              <p style={{textAlign: "center"}}>Please try again.</p>
            </div>
          </div>
        </div>
        <div style={{textAlign: "center"}}>
          <LinkButton href={redirectUrl} primary>Retry</LinkButton>
        </div>
      </div>
    }
    {!isFetchingCampaignFromState && !isFetchingUser && !showRetryButton &&
      <>
      <section style={sectionStyles}>
        <StartNewSubmissionComponent isLoading={isFetchingUser} onSubmit={submitNewSubmissionForm} isReCaptchaVisible={isReCaptchaVisible}/>
      </section>

      {isFetchingSubmissions && !state.user.isTFBAccount &&
      <>
      <div className="container hero3">
        <div className="row">
          <div className="col-md-12" style={{textAlign: "center"}}>
            <LoadingSpinner
              className="mb-3"
              enabled={isFetchingSubmissions}>
            </LoadingSpinner>
            <h2 className="magenta">Loading...</h2><br />
          </div>
        </div>
      </div>
      </>
      }
      {!isFetchingSubmissions && hasSubmissions && 
      <section style={sectionStyles}>
        <TrackerComponent submissions={state.submissions} isLoading={isFetchingSubmissions} error={submissionFetchingErrorMessage} />
      </section>}
      </>
    }
    </>
  )
}

Dashboard.propTypes = {
  location: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired
}

Dashboard.requiresAuth = true
